// client/src/components/billingTransactions/TransactionForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  MenuItem,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Breadcrumbs,
  Link,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingTransactionService from '../../services/billingTransactionService';
import bankAccountService from '../../services/bankAccountService';
import paymentBylawService from '../../services/paymentBylawService';
import paymentItemService from '../../services/paymentItemService';
import billingDetailService from '../../services/billingDetailService';

const TransactionForm = () => {
  const { t, i18n } = useTranslation();
  const { direction } = useContext(LocaleContext);
  const navigate = useNavigate();
  const { id: bylawId, transactionId } = useParams();
  const isEditMode = Boolean(transactionId);
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    payment_bylaw_id: bylawId,
    payment_item_id: '',
    total_value: '',
    distributions: []
  });
  
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [distributionError, setDistributionError] = useState('');
  
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentItems, setPaymentItems] = useState([]);
  const [billingDetails, setBillingDetails] = useState([]);
  const [bylaw, setBylaw] = useState(null);
  
  const [newDistribution, setNewDistribution] = useState({
    bank_account_id: '',
    billing_detail_id: '',
    value: ''
  });
  
  // Edit mode for distribution
  const [editingDistributionIndex, setEditingDistributionIndex] = useState(-1);

  // Current language
  const lang = i18n.language;

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setFetchLoading(true);
        
        // Load bank accounts
        const accountsResponse = await bankAccountService.getBankAccounts();
        setBankAccounts(accountsResponse.data);
        
        // Load bylaw details
        const bylawResponse = await paymentBylawService.getPaymentBylaw(bylawId);
        const bylawData = bylawResponse.data;
        setBylaw(bylawData);
        
        // Load available payment items for this bylaw
        if (!isEditMode) {
          const itemsResponse = await paymentItemService.getAvailableItemsForBylaw(bylawId);
          setPaymentItems(itemsResponse.data);
        } else {
          const itemsResponse = await paymentItemService.getAvailableItemsForBylaw(bylawId, transactionId);
          setPaymentItems(itemsResponse.data);
        }
        
        // Load available billing details for this bylaw
        const detailsResponse = await billingDetailService.getAvailableDetailsForBylaw(bylawId);
        setBillingDetails(detailsResponse.data);

        // Load transaction data if in edit mode
        if (isEditMode) {
          const transactionResponse = await billingTransactionService.getBillingTransaction(transactionId);
          const transaction = transactionResponse.data;
          
          // Format distributions data
          const distributions = transaction.distributions.map(dist => ({
            id: dist.id,
            bank_account_id: dist.bank_account_id,
            billing_detail_id: dist.billing_detail_id || '',
            value: dist.value
          }));
          
          setFormData({
            payment_bylaw_id: bylawId,
            payment_item_id: transaction.payment_item_id,
            total_value: transaction.total_value,
            distributions
          });
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError(isEditMode ? t('errorLoadingTransaction') : t('errorLoadingData'));
        if (isEditMode) {
          navigate(`/payment-bylaws/${bylawId}/transactions`);
        }
      } finally {
        setFetchLoading(false);
      }
    };
    
    loadData();
  }, [isEditMode, bylawId, transactionId, navigate, t]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle changes to new distribution
  const handleDistributionChange = (e) => {
    setNewDistribution({
      ...newDistribution,
      [e.target.name]: e.target.value
    });
  };

  // Start editing a distribution
  const handleEditDistribution = (index) => {
    setEditingDistributionIndex(index);
    setNewDistribution({
      bank_account_id: formData.distributions[index].bank_account_id,
      billing_detail_id: formData.distributions[index].billing_detail_id || '',
      value: formData.distributions[index].value
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingDistributionIndex(-1);
    setNewDistribution({
      bank_account_id: '',
      billing_detail_id: '',
      value: ''
    });
  };

  // Add a new distribution or update existing one
  const handleAddDistribution = () => {
    if (!newDistribution.bank_account_id || !newDistribution.value || parseFloat(newDistribution.value) <= 0) {
      setDistributionError(t('invalidDistribution'));
      return;
    }
    
    // If editing existing distribution
    if (editingDistributionIndex >= 0) {
      const updatedDistributions = [...formData.distributions];
      updatedDistributions[editingDistributionIndex] = {
        ...updatedDistributions[editingDistributionIndex],
        bank_account_id: newDistribution.bank_account_id,
        billing_detail_id: newDistribution.billing_detail_id || null,
        value: parseFloat(newDistribution.value)
      };
      
      setFormData({
        ...formData,
        distributions: updatedDistributions
      });
      
      // Reset edit mode
      setEditingDistributionIndex(-1);
    } else {
      // Check if bank account already has a distribution
      const existingIndex = formData.distributions.findIndex(
        dist => dist.bank_account_id === newDistribution.bank_account_id &&
                dist.billing_detail_id === newDistribution.billing_detail_id
      );
      
      if (existingIndex >= 0) {
        setDistributionError(t('accountAlreadyHasDistribution'));
        return;
      }
      
      setFormData({
        ...formData,
        distributions: [
          ...formData.distributions,
          {
            bank_account_id: newDistribution.bank_account_id,
            billing_detail_id: newDistribution.billing_detail_id || null,
            value: parseFloat(newDistribution.value)
          }
        ]
      });
    }
    
    // Reset new distribution form
    setNewDistribution({
      bank_account_id: '',
      billing_detail_id: '',
      value: ''
    });
    setDistributionError('');
  };

  // Remove a distribution
  const handleRemoveDistribution = (index) => {
    const newDistributions = [...formData.distributions];
    newDistributions.splice(index, 1);
    
    setFormData({
      ...formData,
      distributions: newDistributions
    });
    
    // If removing the distribution we're editing, reset edit mode
    if (index === editingDistributionIndex) {
      setEditingDistributionIndex(-1);
      setNewDistribution({
        bank_account_id: '',
        billing_detail_id: '',
        value: ''
      });
    }
  };

  // Calculate total distribution value
  const calculateTotalDistribution = () => {
    return formData.distributions.reduce((total, dist) => total + parseFloat(dist.value || 0), 0);
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.payment_item_id) {
      setError(t('paymentItemRequired'));
      return false;
    }
    
    if (!formData.total_value || parseFloat(formData.total_value) <= 0) {
      setError(t('validTotalValueRequired'));
      return false;
    }
    
    if (formData.distributions.length === 0) {
      setError(t('distributionsRequired'));
      return false;
    }
    
    const totalDistribution = calculateTotalDistribution();
    if (Math.abs(totalDistribution - parseFloat(formData.total_value)) > 0.01) {
      setError(t('distributionMustEqualTotal'));
      return false;
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    
    try {
      const transactionData = {
        payment_bylaw_id: parseInt(bylawId),
        payment_item_id: parseInt(formData.payment_item_id),
        total_value: parseFloat(formData.total_value),
        distributions: formData.distributions.map(dist => ({
          bank_account_id: parseInt(dist.bank_account_id),
          billing_detail_id: dist.billing_detail_id ? parseInt(dist.billing_detail_id) : null,
          value: parseFloat(dist.value)
        }))
      };
      
      if (isEditMode) {
        await billingTransactionService.updateBillingTransaction(transactionId, transactionData);
        setSuccess(t('transactionUpdatedSuccess'));
      } else {
        await billingTransactionService.createBillingTransaction(transactionData);
        setSuccess(t('transactionCreatedSuccess'));
        
        // Reset form after successful creation
        setFormData({
          payment_bylaw_id: bylawId,
          payment_item_id: '',
          total_value: '',
          distributions: []
        });
      }
    } catch (err) {
      console.error("Submission error:", err);
      setError(isEditMode ? t('errorUpdatingTransaction') : t('errorCreatingTransaction'));
    } finally {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Get bank account name by id
  const getBankAccountName = (id) => {
    const account = bankAccounts.find(acc => acc.id === id);
    return account ? account.name : t('unknownAccount');
  };
  
  // Get billing detail name by id
  const getBillingDetailName = (id) => {
    if (!id) return '';
    const detail = billingDetails.find(detail => detail.id === id);
    return detail ? detail.name : '';
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/payment-bylaws" underline="hover" color="inherit">
          {t('paymentBylaws')}
        </Link>
        <Link 
          component={RouterLink} 
          to={`/payment-bylaws/${bylawId}/transactions`} 
          underline="hover" 
          color="inherit"
        >
          {bylaw?.name || t('bylawTransactions')}
        </Link>
        <Typography color="text.primary">
          {isEditMode ? t('editTransaction') : t('addTransaction')}
        </Typography>
      </Breadcrumbs>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? t('editTransaction') : t('addTransaction')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        {/* Bylaw information */}
        {bylaw && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle1">
              {t('selectedBylaw')}: {bylaw.name}
            </Typography>
            <Typography variant="body2">
              {t('type')}: {t(bylaw.bylaw_type)}
              {bylaw.faculty && ` - ${bylaw.faculty[`name_${lang}`]}`}
            </Typography>
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            {/* Payment Item Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="item-label">{t('paymentItem')}</InputLabel>
                <Select
                  labelId="item-label"
                  id="payment_item_id"
                  name="payment_item_id"
                  value={formData.payment_item_id}
                  label={t('paymentItem')}
                  onChange={handleChange}
                  disabled={isEditMode}
                >
                  <MenuItem value="">{t('selectPaymentItem')}</MenuItem>
                  {paymentItems.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name} - {t(item.item_type)}
                      {item.faculty && ` - ${item.faculty[`name_${lang}`]}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Total Value */}
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="total_value"
                name="total_value"
                label={t('totalValue')}
                type="number"
                value={formData.total_value}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" gutterBottom>
            {t('valueDistribution')}
          </Typography>
          
          {distributionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {distributionError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                id="bank_account_id"
                name="bank_account_id"
                label={t('bankAccount')}
                value={newDistribution.bank_account_id}
                onChange={handleDistributionChange}
              >
                <MenuItem value="">{t('selectBankAccount')}</MenuItem>
                {bankAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} - {account.account_number}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                id="billing_detail_id"
                name="billing_detail_id"
                label={t('billingDetail')}
                value={newDistribution.billing_detail_id}
                onChange={handleDistributionChange}
              >
                <MenuItem value="">{t('noBillingDetail')}</MenuItem>
                {billingDetails.map((detail) => (
                  <MenuItem key={detail.id} value={detail.id}>
                    {detail.name} - {t(detail.detail_type)}
                    {detail.faculty && ` - ${detail.faculty[`name_${lang}`]}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                id="value"
                name="value"
                label={t('value')}
                type="number"
                value={newDistribution.value}
                onChange={handleDistributionChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              {editingDistributionIndex >= 0 ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleAddDistribution}
                    startIcon={<SaveIcon />}
                    sx={{ height: '56px' }}
                  >
                    {t('update')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancelEdit}
                    sx={{ height: '56px' }}
                  >
                    {t('cancel')}
                  </Button>
                </Box>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAddDistribution}
                  startIcon={<AddIcon />}
                  sx={{ height: '56px' }}
                >
                  {t('add')}
                </Button>
              )}
            </Grid>
          </Grid>
          
          {formData.distributions.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('bankAccount')}</TableCell>
                    <TableCell>{t('billingDetail')}</TableCell>
                    <TableCell align="right">{t('value')}</TableCell>
                    <TableCell align="center">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.distributions.map((distribution, index) => (
                    <TableRow key={index}>
                      <TableCell>{getBankAccountName(distribution.bank_account_id)}</TableCell>
                      <TableCell>
                        {distribution.billing_detail_id ? 
                          getBillingDetailName(distribution.billing_detail_id) : 
                          <Typography variant="body2" color="text.secondary">{t('noBillingDetail')}</Typography>
                        }
                      </TableCell>
                      <TableCell align="right">{formatCurrency(distribution.value)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleEditDistribution(index)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveDistribution(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('total')}</TableCell>
                    <TableCell></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(calculateTotalDistribution())}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('noDistributionsAdded')}
            </Alert>
          )}
          
          {formData.total_value && formData.distributions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {Math.abs(calculateTotalDistribution() - parseFloat(formData.total_value)) > 0.01 ? (
                <Alert severity="warning">
                  {t('distributionTotalMismatch', {
                    distributionTotal: formatCurrency(calculateTotalDistribution()),
                    totalValue: formatCurrency(formData.total_value)
                  })}
                </Alert>
              ) : (
                <Alert severity="success">
                  {t('distributionTotalMatches')}
                </Alert>
              )}
            </Box>
          )}
          
          <Box sx={{ 
            mt: 3, 
            display: 'flex',
            gap: 2,
            justifyContent: direction === 'ltr' ? 'flex-end' : 'flex-start'
          }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/payment-bylaws/${bylawId}/transactions`)}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? t('saving') : t('save')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default TransactionForm;